// GeoLocationContext.tsx
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { captureException, captureMessage } from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { log } from 'constants/config';
import { isGeoBlockedRegion, shouldHardBlock } from 'constants/geoBlocking';
import { apiRateLimiter, getSessionId, isBot } from 'utils/client/botDetection';
import { hasLocationPermission } from 'utils/client/hasLocationPermission';
import { requestUserLocation } from 'utils/client/requestUserLocation';
import { extractAddressParts } from 'utils/shared/geo/extractAddressParts';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useViewer } from 'hooks/useViewer';
import { autocompleteManager } from './autocompleteManager';
import { geoCache } from './cache';
import {
  AUTOCOMPLETE_CONFIG,
  DEFAULT_FALLBACK_LOCATION,
  GEO_FEATURE_FLAGS,
  logPaidRequest,
} from './config';
import { googleMapsLoader } from './googleMapsLoader';
import { locationManager } from './locationManager';
import { locationStorage } from './locationStorage';

const IS_CAPTURING_ERRORS = false;

// Track function calls to detect infinite loops
const callCounter: Record<string, number> = {};
const trackCall = (name: string) => {
  callCounter[name] = (callCounter[name] || 0) + 1;
  if (callCounter[name] > 10) {
    log(`⚠️ [POTENTIAL INFINITE LOOP] ${name} called ${callCounter[name]} times!`);
  }
  return callCounter[name];
};

export interface Position {
  latitude: number;
  longitude: number;
}

interface GeoLocationState {
  // Core state
  position: Position | null;
  addressString: string;
  exactAddress: google.maps.places.PlaceResult | null;
  isExactCenter: boolean;
  errorRequestLocation: null | Error | PositionErrorCallback | GeolocationPositionError;

  // Loading states
  isLoadingAutocomplete: boolean;
  isAddressPending: boolean;
  isInitializing: boolean;

  // Autocomplete state
  suggestedAddresses: google.maps.places.AutocompletePrediction[];
  activeAutocompleteAddress: google.maps.places.AutocompletePrediction | null;
  autocompleteInputLength: number;

  // Session state
  hasSetLocationEstimate: boolean;
  hasSetExactLocation: boolean;
  hasPromptedThisSession: boolean;

  // Computed values
  centerLatitude: number;
  centerLongitude: number;

  // Geo-blocking state (for CN, SG countries)
  isGeoBlocked: boolean;
  geoBlockChecked: boolean;

  // Google Maps availability
  isGoogleMapsAvailable: boolean;
}

interface GeoLocationActions {
  // Core actions
  setPosition: (position: Position | null) => void;
  setAddressString: (address: string) => void;
  setExactAddress: (exactAddress: google.maps.places.PlaceResult | null) => void;
  setIsExactCenter: (isExactCenter: boolean) => void;
  setErrorRequestLocation: (
    error: null | Error | PositionErrorCallback | GeolocationPositionError,
  ) => void;

  // Business logic methods (EXACT SAME NAMES as original hook)
  initializeGeoLocation: (params?: {
    skipLocationEstimation?: boolean;
    initialConfig?: { position?: Position; addressString?: string; isExactCenter?: boolean };
  }) => Promise<void>;
  requestUserLocationAndUpdateState: () => Promise<Position | null>;
  getEstimatedLocation: () => Promise<Position | null>;
  getAddressFromLatLng: (latitude: number, longitude: number) => Promise<string | null>;

  // Autocomplete methods (EXACT SAME NAMES)
  getAutocompletePlaces: (input: string) => void;
  getImmediateAutcompletePlaces: (
    input: string,
  ) => Promise<google.maps.places.AutocompletePrediction[]>;
  handleSubmitAutocomplete: (
    address?: google.maps.places.AutocompletePrediction | null,
    syncLatLngToURL?: boolean,
  ) => Promise<Position | void>;
  getExactDetails: (placeId: string) => Promise<google.maps.places.PlaceResult | null>;

  // Business convenience methods (moved from hook to provider)
  updateAddressAndClearAutocomplete: (address: string) => void;
  selectAutocompleteSuggestion: (
    suggestion: google.maps.places.AutocompletePrediction,
  ) => Promise<Position | void>;
  clearAutocompleteState: () => void;
  resetLocationState: () => void;
  shouldShowAutocomplete: () => boolean;
  isSearchingAutocomplete: () => boolean;
  onMapDrag: (position: Position) => void;
  // Utility setters (EXACT SAME NAMES)
  setHasPromptedThisSession: React.Dispatch<React.SetStateAction<boolean>>;
  setHasSetLocationEstimate: (hasSetLocationEstimate: boolean) => void;
  setHasSetExactLocation: (hasSetExactLocation: boolean) => void;
  setSuggestedAddresses: (addresses: google.maps.places.AutocompletePrediction[]) => void;
  setActiveAutocompleteAddress: (address: google.maps.places.AutocompletePrediction | null) => void;
}

type GeoLocationContextType = GeoLocationState & GeoLocationActions;

const GeoLocationContext = createContext<GeoLocationContextType | undefined>(undefined);

interface Props {
  children: ReactNode;
  autocompleteDebounceTime?: number;
  enableAutoInitialization?: boolean;
}

export const GeoLocationProvider = ({
  children,
  autocompleteDebounceTime = AUTOCOMPLETE_CONFIG.DEFAULT_DEBOUNCE,
  enableAutoInitialization = true,
}: Props) => {
  const router = useRouter();

  // Core state
  const [position, setPosition] = useState<Position | null>(null);
  const [addressString, setAddressString] = useState<string>('');
  const [exactAddress, setExactAddress] = useState<google.maps.places.PlaceResult | null>(null);
  const [isExactCenter, setIsExactCenter] = useState(false);
  const [errorRequestLocation, setErrorRequestLocation] = useState<
    null | Error | PositionErrorCallback | GeolocationPositionError
  >(null);

  // Loading states
  const [isLoadingAutocomplete, setIsLoadingAutocomplete] = useState(false);
  const [isAddressPending, setIsAddressPending] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  // Autocomplete state
  const [suggestedAddresses, setSuggestedAddresses] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [activeAutocompleteAddress, setActiveAutocompleteAddress] =
    useState<google.maps.places.AutocompletePrediction | null>(null);
  const [autocompleteInputLength, setAutocompleteInputLength] = useState(0);

  // Session state
  const [hasSetLocationEstimate, setHasSetLocationEstimate] = useState(false);
  const [hasSetExactLocation, setHasSetExactLocation] = useState(false);
  const [hasPromptedThisSession, setHasPromptedThisSession] = useState(false);

  // Geo-blocking state (for CN, SG countries)
  const [isGeoBlocked, setIsGeoBlocked] = useState(false);
  const [geoBlockChecked, setGeoBlockChecked] = useState(false);

  const initializationState = useRef<{
    isInitializing: boolean;
    hasInitialized: boolean;
    initializationPromise: Promise<void> | null;
    skippedEstimation: boolean;
  }>({
    isInitializing: false,
    hasInitialized: false,
    initializationPromise: null,
    skippedEstimation: false,
  });

  // Refs for cleanup and state management
  const hookId = useRef(Math.random().toString(36).substring(7));
  const pendingRequestRef = useRef<AbortController | null>(null);

  const user = useGetCurrentUser();
  const viewer = useViewer();
  const geometry = user.user?.geometry;

  // Check if bots should be blocked
  const isBotUser = GEO_FEATURE_FLAGS.ENABLE_BOT_DETECTION && isBot();
  const sessionId = getSessionId();

  log('[GeoLocationProvider] Initializing with config:', {
    autocompleteDebounceTime,
    featureFlags: GEO_FEATURE_FLAGS,
    hookId: hookId.current,
    isBotUser,
    sessionId,
    hasUserGeometry: !!geometry,
    currentPosition: position,
    currentAddress: addressString,
  });

  // Check geo-blocking status on mount (for CN, SG countries)
  // Uses synchronous timezone/locale detection - no API call, zero latency impact
  useEffect(() => {
    if (!GEO_FEATURE_FLAGS.ENABLE_GEO_BLOCKING) {
      setGeoBlockChecked(true);
      return;
    }

    // Synchronous check - same detection as bot/fraud detection
    const blocked = isGeoBlockedRegion();
    setIsGeoBlocked(blocked);
    setGeoBlockChecked(true);

    if (blocked) {
      log('[GeoLocationProvider] Geo-blocked region detected, using fallback location');
      setPosition(DEFAULT_FALLBACK_LOCATION.position);
      setAddressString(DEFAULT_FALLBACK_LOCATION.addressString);
      setIsExactCenter(false);
    }
  }, []);

  // Helper function for waiting for Google Maps
  const waitForGoogleMaps = async (maxAttempts = 50): Promise<boolean> => {
    for (let i = 0; i < maxAttempts; i++) {
      if (window.google?.maps?.Geocoder) {
        if (i > 0) {
          console.log('✅ Google Maps loaded after', i * 100, 'ms');
        }
        return true;
      }
      if (i === 0) {
        console.log('⏳ Waiting for Google Maps to load...');
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    console.warn(
      '⚠️ Google Maps did not load in time (after',
      maxAttempts * 100,
      'ms), using fallback',
    );
    return false;
  };

  // Business logic methods (moved from hook)
  const getAddressFromLatLng = useCallback(
    async (latitude: number, longitude: number): Promise<string | null> => {
      const callCount = trackCall('getAddressFromLatLng');
      log(`[getAddressFromLatLng] Called (${callCount}x) with:`, {
        latitude,
        longitude,
        isBotUser,
      });

      // Bots get generic location
      // Check directly to avoid unnecessary dependency
      if (GEO_FEATURE_FLAGS.ENABLE_BOT_DETECTION && isBot()) {
        log('[getAddressFromLatLng] Bot detected, returning default address');
        const address = DEFAULT_FALLBACK_LOCATION.addressString;
        setAddressString(address);
        return address;
      }

      // Geo-blocked users get the fallback location
      // Check directly to avoid race condition with state
      if (GEO_FEATURE_FLAGS.ENABLE_GEO_BLOCKING && isGeoBlockedRegion()) {
        log('[getAddressFromLatLng] Geo-blocked, returning fallback address');
        const address = DEFAULT_FALLBACK_LOCATION.addressString;
        setAddressString(address);
        return address;
      }

      // Check cache first
      const cachedAddress = geoCache.getGeocoding(latitude, longitude);
      if (cachedAddress) {
        log('[getAddressFromLatLng] Cache hit:', cachedAddress);
        setAddressString(cachedAddress);
        setIsAddressPending(false);
        return cachedAddress;
      }

      // Rate limiting
      const rateLimitKey = `geocode:${sessionId}`;
      if (GEO_FEATURE_FLAGS.ENABLE_RATE_LIMITING) {
        const allowed = apiRateLimiter.isAllowed(rateLimitKey);
        if (!allowed) {
          console.error('🚨🚨🚨 LOCATION SELECTED - RATE LIMIT HIT 🚨🚨🚨', {
            rateLimitKey,
            sessionId,
            timestamp: new Date().toISOString(),
            caller: 'getAddressFromLatLng',
            coordinates: { latitude, longitude },
          });
          setAddressString('Location selected');
          return null;
        }
      }

      try {
        // Ensure Google Maps is loaded before geocoding
        if (!isBotUser) {
          try {
            await googleMapsLoader.ensureLoaded();
            await waitForGoogleMaps();
          } catch (error) {
            console.error('Failed to load Google Maps for geocoding:', error);
          }
        }

        const googleMapsLoaded = googleMapsLoader.isGoogleMapsLoaded();

        console.log('📊 GEOCODING METHOD CHECK', {
          hasGoogle: !!window.google,
          hasGoogleMaps: !!window.google?.maps,
          hasGeocoder: !!window.google?.maps?.Geocoder,
          googleMapsLoaded,
          timestamp: new Date().toISOString(),
          caller: 'getAddressFromLatLng',
        });

        if (googleMapsLoaded && window.google && window.google.maps) {
          return new Promise<string | null>((resolve) => {
            const geocoder = new window.google.maps.Geocoder();

            logPaidRequest('Geocoding (Client Library)', {
              coordinates: { latitude, longitude },
              sessionId,
              rateLimitKey: `geocode:${sessionId}`,
              costEstimate: '$0.005 per request',
              apiEndpoint: 'Geocoder.geocode',
              method: 'Google Maps JavaScript API',
              cacheStatus: 'MISS',
              routePattern: router.route,
            });

            geocoder.geocode(
              { location: { lat: latitude, lng: longitude } },
              (
                results: google.maps.GeocoderResult[] | null,
                status: google.maps.GeocoderStatus,
              ) => {
                if (status === 'OK' && results && results.length > 0) {
                  const addressParts = extractAddressParts(results[0]);
                  const addressString = `${addressParts.city}, ${addressParts.state} ${addressParts.zipcode}`;
                  setAddressString(addressString);
                  setIsAddressPending(false);
                  geoCache.setGeocoding(latitude, longitude, addressString);
                  resolve(addressString);
                } else {
                  console.error('🚨🚨🚨 LOCATION SELECTED - GEOCODING FAILED 🚨🚨🚨', {
                    status,
                    coordinates: { latitude, longitude },
                    timestamp: new Date().toISOString(),
                    caller: 'getAddressFromLatLng - Google Maps Geocoder',
                  });
                  setAddressString('Location selected');
                  resolve(null);
                }
              },
            );
          });
        }

        // Fall back to the original fetch method if Google Maps API isn't available
        logPaidRequest('Geocoding (Direct HTTP)', {
          coordinates: { latitude, longitude },
          sessionId,
          rateLimitKey: `geocode:${sessionId}`,
          costEstimate: '$0.005 per request',
          apiEndpoint: '/maps/api/geocode/json',
          method: 'Direct HTTP Request',
          googleMapsAvailable: !!window.google?.maps,
          warning: 'Using fallback - this costs more than client library',
          cacheStatus: 'MISS',
        });

        if (!process.env.GOOGLE_MAPS_API_KEY) {
          console.error('🚨 GOOGLE_MAPS_API_KEY is not defined');
          setAddressString('Location selected');
          setIsAddressPending(false);
          return null;
        }

        const addressFromLatLngResponse = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.GOOGLE_MAPS_API_KEY}`,
        );
        const addressFromLatLng = await addressFromLatLngResponse.json();

        if (addressFromLatLng.results && addressFromLatLng.results.length > 0) {
          const addressParts = extractAddressParts(addressFromLatLng.results[0]);
          const addressString = `${addressParts.city}, ${addressParts.state} ${addressParts.zipcode}`;
          setAddressString(addressString);
          setIsAddressPending(false);
          geoCache.setGeocoding(latitude, longitude, addressString);
          return addressString;
        } else if (addressFromLatLng.status === 'REQUEST_DENIED') {
          console.error('🚨🚨🚨 LOCATION SELECTED - REQUEST DENIED 🚨🚨🚨', {
            status: addressFromLatLng.status,
            coordinates: { latitude, longitude },
            timestamp: new Date().toISOString(),
            caller: 'getAddressFromLatLng - Direct API',
          });
          setAddressString('Location selected');
          return null;
        }

        console.error('🚨🚨🚨 LOCATION SELECTED - NO RESULTS 🚨🚨🚨', {
          results: addressFromLatLng.results,
          coordinates: { latitude, longitude },
          timestamp: new Date().toISOString(),
          caller: 'getAddressFromLatLng - Direct API',
        });
        setAddressString('Location selected');
        return null;
      } catch (error) {
        console.error('🚨🚨🚨 LOCATION SELECTED - EXCEPTION 🚨🚨🚨', {
          error,
          coordinates: { latitude, longitude },
          timestamp: new Date().toISOString(),
          caller: 'getAddressFromLatLng - Catch block',
        });
        setAddressString('Location selected');
        return null;
      }
    },
    [sessionId, router.route],
  );

  const requestUserLocationAndUpdateState = useCallback(async (): Promise<Position | null> => {
    // Bots get default location
    // Check directly to avoid unnecessary dependency
    if (GEO_FEATURE_FLAGS.ENABLE_BOT_DETECTION && isBot()) {
      console.warn('Bot detected, using default location');
      const { position, addressString } = DEFAULT_FALLBACK_LOCATION;
      setPosition(position);
      setAddressString(addressString);
      setIsExactCenter(false);
      return position;
    }

    // Check if we already have a stored location
    if (GEO_FEATURE_FLAGS.USE_LOCATION_CONTEXT) {
      const stored = locationStorage.getStoredLocation();
      if (stored && stored.source === 'browser_geolocation') {
        setPosition(stored.position);
        setAddressString(stored.addressString);
        setIsExactCenter(true);
        return stored.position;
      }
    }

    // Try using the utility function first
    try {
      const geo = await requestUserLocation();
      if (geo) {
        const latitude = geo.coords.latitude;
        const longitude = geo.coords.longitude;
        const newPosition = { latitude, longitude };

        setPosition(newPosition);
        setIsExactCenter(true);

        getAddressFromLatLng(latitude, longitude).then((address) => {
          if (address && GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) {
            locationStorage.setStoredLocation(newPosition, address, 'browser_geolocation');
          }
        });
        return newPosition;
      }
    } catch (error) {
      console.error('requestUserLocation utility failed:', error);
    }

    // Fall back to direct navigator.geolocation
    return new Promise<Position | null>((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (geo) => {
            const latitude = geo.coords.latitude;
            const longitude = geo.coords.longitude;
            const newPosition = { latitude, longitude };

            setPosition(newPosition);
            setIsExactCenter(true);

            getAddressFromLatLng(latitude, longitude).then((address) => {
              if (address && GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) {
                locationStorage.setStoredLocation(newPosition, address, 'browser_geolocation');
              }
            });

            resolve(newPosition);
          },
          (error) => {
            console.error('navigator.geolocation error:', error);
            setErrorRequestLocation(error);
            resolve(null);
          },
        );
      } else {
        console.warn('navigator.geolocation not available');
        resolve(null);
      }
    });
  }, [getAddressFromLatLng]);

  const getEstimatedLocation = useCallback(async (): Promise<Position | null> => {
    const callCount = trackCall('getEstimatedLocation');
    log(`[getEstimatedLocation] Called (${callCount}x), isBotUser:`, isBotUser);

    // Use default location for bots
    // Check directly to avoid unnecessary dependency
    if (GEO_FEATURE_FLAGS.ENABLE_BOT_DETECTION && isBot()) {
      log('[getEstimatedLocation] Bot detected, using default location');
      const { position, addressString } = DEFAULT_FALLBACK_LOCATION;
      setPosition(position);
      setAddressString(addressString);
      setIsExactCenter(false);
      return position;
    }

    // Geo-blocked users get the fallback location
    // Check directly to avoid race condition with state
    if (GEO_FEATURE_FLAGS.ENABLE_GEO_BLOCKING && isGeoBlockedRegion()) {
      log('[getEstimatedLocation] Geo-blocked, using the fallback location');
      const { position, addressString } = DEFAULT_FALLBACK_LOCATION;
      setPosition(position);
      setAddressString(addressString);
      setIsExactCenter(false);
      return position;
    }

    // Check for stored location
    if (GEO_FEATURE_FLAGS.USE_LOCATION_CONTEXT) {
      const stored = locationStorage.getStoredLocation();
      if (stored) {
        log('[getEstimatedLocation] Using stored location:', stored);
        setPosition(stored.position);
        setAddressString(stored.addressString);
        setIsExactCenter(
          stored.source === 'browser_geolocation' || stored.source === 'user_search',
        );
        return stored.position;
      }
    }

    // Use IP-based geolocation if enabled
    if (GEO_FEATURE_FLAGS.USE_IP_GEOLOCATION) {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        if (data.latitude && data.longitude) {
          const newPosition = {
            latitude: data.latitude,
            longitude: data.longitude,
          };
          const addressString = `${data.city}, ${data.region_code} ${data.postal || ''}`.trim();

          setPosition(newPosition);
          setAddressString(addressString);
          setIsExactCenter(false);

          if (GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) {
            locationStorage.setStoredLocation(newPosition, addressString, 'google_estimation');
          }

          geoCache.setGeocoding(data.latitude, data.longitude, addressString);
          return newPosition;
        }
      } catch (error) {
        console.error('IP geolocation failed:', error);
      }
    }

    // Use singleton location manager for Google geolocation API
    try {
      const result = await locationManager.getEstimatedLocation(
        sessionId,
        getAddressFromLatLng,
        (position, address) => {
          log('[getEstimatedLocation] Address update callback - setting address:', address);
          setAddressString(address);
          setIsAddressPending(false);

          if (GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) {
            locationStorage.setStoredLocation(position, address, 'google_estimation');
          }
        },
      );

      log('[getEstimatedLocation] Location manager returned:', result);

      setPosition(result.position);
      setIsExactCenter(false);

      if (result.address) {
        setAddressString(result.address);
        setIsAddressPending(false);

        if (GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) {
          locationStorage.setStoredLocation(result.position, result.address, result.source);
        }
      } else {
        log('[getEstimatedLocation] Address pending, will be set when geocoding completes');
        setIsAddressPending(true);
        setAddressString('');

        const timeoutId = setTimeout(() => {
          setIsAddressPending((pending) => {
            if (pending) {
              log('[getEstimatedLocation] Address geocoding timeout - clearing pending state');
              setAddressString('Location selected');
              return false;
            }
            return pending;
          });
        }, 10000);

        pendingRequestRef.current = { abort: () => clearTimeout(timeoutId) } as any;

        if (GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) {
          locationStorage.setStoredLocation(result.position, '', result.source);
        }
      }

      return result.position;
    } catch (error) {
      if (error instanceof Error) {
        setErrorRequestLocation(error);
      } else if (error && typeof error === 'object' && 'code' in error) {
        setErrorRequestLocation(error as GeolocationPositionError);
      } else {
        setErrorRequestLocation(new Error('Failed to get estimated location'));
      }
      return null;
    }
  }, [sessionId, getAddressFromLatLng]);

  const getAutocompletePlaces = useCallback(
    (input: string): void => {
      log('🎯 [getAutocompletePlaces] Called with:', {
        input,
        length: input.trim().length,
        minLength: AUTOCOMPLETE_CONFIG.MIN_LENGTH,
        currentLoadingState: isLoadingAutocomplete,
        timestamp: new Date().toISOString(),
      });

      setAutocompleteInputLength(input.trim().length);

      // Only block in hard mode - soft mode allows autocomplete for false positive handling
      if (shouldHardBlock()) {
        log('[getAutocompletePlaces] Hard blocked, returning empty');
        captureMessage('Autocomplete blocked: geo-blocked region', {
          level: 'warning',
          extra: {
            input: input.substring(0, 50),
            isGeoBlocked: true,
          },
        });
        setSuggestedAddresses([]);
        setIsLoadingAutocomplete(false);
        return;
      }

      if (input.trim().length < AUTOCOMPLETE_CONFIG.MIN_LENGTH) {
        log(
          '[getAutocompletePlaces] Input too short:',
          input.length,
          'min:',
          AUTOCOMPLETE_CONFIG.MIN_LENGTH,
        );
        setSuggestedAddresses([]);
        setIsLoadingAutocomplete(false);
        return;
      }

      if (
        GEO_FEATURE_FLAGS.ENABLE_RATE_LIMITING &&
        !apiRateLimiter.isAllowed(`autocomplete:${sessionId}`)
      ) {
        log('[getAutocompletePlaces] Rate limit exceeded');
        captureMessage('Autocomplete blocked: rate limit exceeded', {
          level: 'warning',
          extra: {
            sessionId,
            input: input.substring(0, 50),
          },
        });
        setIsLoadingAutocomplete(false);
        return;
      }

      log('🎯 [getAutocompletePlaces] Setting loading to TRUE');
      setIsLoadingAutocomplete(true);

      autocompleteManager.search(
        input,
        sessionId,
        (predictions) => {
          log('✅ [Autocomplete] Results received:', {
            input,
            count: predictions.length,
            timestamp: new Date().toISOString(),
          });
          setSuggestedAddresses(predictions);
          log('🎯 [getAutocompletePlaces] Setting loading to FALSE');
          setIsLoadingAutocomplete(false);
        },
        (error) => {
          log('[Autocomplete] Error:', error);
          setSuggestedAddresses([]);
          log('🎯 [getAutocompletePlaces] Setting loading to FALSE (error)');
          setIsLoadingAutocomplete(false);
        },
      );
    },
    [sessionId, isLoadingAutocomplete],
  );

  const getImmediateAutcompletePlaces = useCallback(
    async (input: string): Promise<google.maps.places.AutocompletePrediction[]> => {
      return new Promise((resolve) => {
        getAutocompletePlaces(input);
        resolve(suggestedAddresses);
      });
    },
    [getAutocompletePlaces, suggestedAddresses],
  );

  const getExactDetails = useCallback(
    (placeId: string): Promise<google.maps.places.PlaceResult | null> => {
      // Only block in hard mode - soft mode allows place details for false positive handling
      if (shouldHardBlock()) {
        log('[getExactDetails] Hard blocked, returning null');
        return Promise.resolve(null);
      }

      const cached = geoCache.getPlaceDetails(placeId);
      if (cached) {
        return Promise.resolve(cached);
      }

      if (
        GEO_FEATURE_FLAGS.ENABLE_RATE_LIMITING &&
        !apiRateLimiter.isAllowed(`placeDetails:${sessionId}`)
      ) {
        console.warn('Rate limit exceeded for place details');
        return Promise.resolve(null);
      }

      return new Promise((resolve) => {
        const service = new google.maps.places.PlacesService(document.createElement('div'));
        logPaidRequest('Place Details', {
          placeId,
          sessionId,
          costEstimate: '$0.017 per request',
          apiEndpoint: 'PlacesService.getDetails',
          cacheStatus: 'MISS',
        });

        service.getDetails({ placeId: placeId }, (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            if (place) {
              geoCache.setPlaceDetails(placeId, place);
            }
            resolve(place);
          } else {
            if (IS_CAPTURING_ERRORS) {
              captureException(new Error('Google Maps Error:' + ' ' + status));
            }
            resolve(null);
          }
        });
      });
    },
    [sessionId],
  );

  const handleSubmitAutocomplete = useCallback(
    async (
      address?: google.maps.places.AutocompletePrediction | null,
      syncLatLngToURL: boolean = true,
    ): Promise<Position | void> => {
      const addressText = address?.description || addressString;
      setAddressString(addressText);

      try {
        let addressForFetch = address;

        if (!addressForFetch && addressString) {
          const finalAddress = await getImmediateAutcompletePlaces(addressString);
          addressForFetch = finalAddress[0];
          setActiveAutocompleteAddress(addressForFetch);
        }

        if (!addressForFetch) {
          console.error('No address to geocode');
          return;
        }

        console.log('💡 OPTIMIZATION: Using Geocoding instead of Place Details', {
          address: addressForFetch.description,
          savedCost: '$0.012 per request',
          oldCost: '$0.017 (Place Details)',
          newCost: '$0.005 (Geocoding)',
        });

        const cachedCoords = geoCache.getGeocodingByAddress(addressForFetch.description);
        if (cachedCoords) {
          setPosition(cachedCoords);
          setIsExactCenter(true);

          // ✅ ADD: URL sync for cached coordinates
          if (syncLatLngToURL && GEO_FEATURE_FLAGS.PERSIST_COORDINATES_TO_URL) {
            try {
              updateURL(cachedCoords);
            } catch (error) {
              console.error(
                '[handleSubmitAutocomplete] Failed to sync cached position to URL:',
                error,
              );
            }
          }

          return cachedCoords;
        }

        try {
          await googleMapsLoader.ensureLoaded();
        } catch (error) {
          console.error('Failed to load Google Maps for address geocoding:', error);
        }

        if (googleMapsLoader.isGoogleMapsLoaded() && window.google?.maps?.Geocoder) {
          const geocoder = new window.google.maps.Geocoder();

          logPaidRequest('Geocoding (From Address)', {
            address: addressForFetch.description,
            sessionId,
            costEstimate: '$0.005 per request',
            apiEndpoint: 'Geocoder.geocode',
            method: 'Address to Coordinates',
            optimization: 'Replaced Place Details API - saved $0.012',
          });

          const result = await new Promise<google.maps.GeocoderResult | null>((resolve) => {
            geocoder.geocode(
              { address: addressForFetch.description },
              (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
                if (status === 'OK' && results && results.length > 0) {
                  resolve(results[0]);
                } else {
                  console.error('Geocoding failed:', status);
                  resolve(null);
                }
              },
            );
          });

          if (result && result.geometry && result.geometry.location) {
            const newLatitude = result.geometry.location.lat();
            const newLongitude = result.geometry.location.lng();
            const newPosition = { latitude: newLatitude, longitude: newLongitude };

            setPosition(newPosition);
            setIsExactCenter(true);
            setExactAddress(result as any);

            geoCache.setGeocoding(newLatitude, newLongitude, addressForFetch.description);
            geoCache.setGeocodingByAddress(addressForFetch.description, newLatitude, newLongitude);

            if (GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) {
              locationStorage.setStoredLocation(
                newPosition,
                addressForFetch.description,
                'user_search',
              );
              log('📍 [handleSubmitAutocomplete] Stored location for navigation:', {
                position: newPosition,
                address: addressForFetch.description,
              });
            }

            // ✅ ADD: URL sync for geocoded position
            if (GEO_FEATURE_FLAGS.PERSIST_COORDINATES_TO_URL) {
              try {
                updateURL(newPosition);
              } catch (error) {
                console.error(
                  '[handleSubmitAutocomplete] Failed to sync geocoded position to URL:',
                  error,
                );
              }
            }

            return newPosition;
          }
        } else {
          console.warn('Google Maps not loaded, using direct API');
        }
      } catch (error) {
        console.error('Error in handleSubmitAutocomplete:', error);
      }
    },
    [addressString, getImmediateAutcompletePlaces, sessionId],
  );

  // Business convenience methods - these manipulate state and should be in provider
  const updateAddressAndClearAutocomplete = useCallback((address: string) => {
    setAddressString(address);
    setActiveAutocompleteAddress(null);
    setSuggestedAddresses([]);
  }, []);

  const selectAutocompleteSuggestion = useCallback(
    async (suggestion: google.maps.places.AutocompletePrediction) => {
      setActiveAutocompleteAddress(suggestion);
      setAddressString(suggestion.description);
      setSuggestedAddresses([]); // Clear suggestions after selection
      return handleSubmitAutocomplete(suggestion);
    },
    [handleSubmitAutocomplete],
  );

  const clearAutocompleteState = useCallback(() => {
    setSuggestedAddresses([]);
    setActiveAutocompleteAddress(null);
    setAddressString('');
  }, []);

  const resetLocationState = useCallback(() => {
    setPosition(null);
    setAddressString('');
    setExactAddress(null);
    setActiveAutocompleteAddress(null);
    setSuggestedAddresses([]);
    setIsExactCenter(false);
    setErrorRequestLocation(null);
  }, []);

  const shouldShowAutocomplete = useCallback(() => {
    return (
      autocompleteInputLength >= AUTOCOMPLETE_CONFIG.MIN_LENGTH && suggestedAddresses.length > 0
    );
  }, [autocompleteInputLength, suggestedAddresses.length]);

  const isSearchingAutocomplete = useCallback(() => {
    return autocompleteInputLength >= AUTOCOMPLETE_CONFIG.MIN_LENGTH && isLoadingAutocomplete;
  }, [autocompleteInputLength, isLoadingAutocomplete]);

  const initializeGeoLocation = useCallback(
    async (
      params: {
        skipLocationEstimation?: boolean;
        initialConfig?: {
          position?: Position;
          addressString?: string;
          isExactCenter?: boolean;
        };
      } = {},
    ): Promise<void> => {
      const { skipLocationEstimation = false, initialConfig = {} } = params;
      // CHECK 1: Currently initializing (in progress)
      if (initializationState.current.isInitializing) {
        log('[initializeGeoLocation] Already initializing, waiting for completion...');

        // Wait for current initialization to complete
        await initializationState.current.initializationPromise!;

        // After completion, check if we need to upgrade to full estimation or override with initialConfig
        if (
          (initializationState.current.skippedEstimation && !skipLocationEstimation) ||
          initialConfig?.position
        ) {
          const reason = initialConfig?.position
            ? 'Override with initialConfig'
            : 'Upgrading to full estimation after concurrent run';

          log(`[initializeGeoLocation] ${reason}`);
          // Reset and run full estimation
          initializationState.current.hasInitialized = false;
          initializationState.current.skippedEstimation = false;
          return initializeGeoLocation({
            skipLocationEstimation,
            initialConfig: initialConfig,
          }); // Recursive call
        }

        return Promise.resolve();
      }

      // CHECK 2: Already completed initialization (add this NEW check)
      if (initializationState.current.hasInitialized && initialConfig?.position) {
        const hasExistingPosition = !!position;
        const isDifferentPosition =
          hasExistingPosition &&
          (Math.abs(position.latitude - initialConfig.position.latitude) > 0.0001 ||
            Math.abs(position.longitude - initialConfig.position.longitude) > 0.0001);

        // Reinitialize if we have no position OR if the new position is significantly different
        if (!hasExistingPosition || isDifferentPosition) {
          log(
            '[initializeGeoLocation] Already initialized, but reinitializing with new initialConfig:',
            {
              hasExistingPosition,
              isDifferentPosition,
              currentPosition: position,
              newPosition: initialConfig.position,
              newAddress: initialConfig.addressString,
            },
          );

          // Reset and run with new initial config
          initializationState.current.hasInitialized = false;
          initializationState.current.skippedEstimation = false;

          return initializeGeoLocation({
            skipLocationEstimation: skipLocationEstimation,
            initialConfig: initialConfig,
          });
        } else {
          log('[initializeGeoLocation] Already initialized with same position, skipping reinit');
          return Promise.resolve();
        }
      }

      initializationState.current.isInitializing = true;
      setIsInitializing(true);

      initializationState.current.initializationPromise = (async () => {
        try {
          console.log('🚀 LOCATION INITIALIZATION STARTED', {
            timestamp: new Date().toISOString(),
            sessionId,
            isSessionLoading: viewer.isSessionLoading,
            hasExistingPosition: !!position,
            hasUserGeometry: !!geometry,
            userId: viewer.userId,
            hookInstanceId: hookId.current,
            isBotUser,
          });

          // 🤖 HIGHEST PRIORITY: Bot detection - immediate default location
          // Check directly to avoid unnecessary dependency
          if (GEO_FEATURE_FLAGS.ENABLE_BOT_DETECTION && isBot()) {
            log('[initializeGeoLocation] Bot detected, using default location');
            const { position: botPosition, addressString } = DEFAULT_FALLBACK_LOCATION;

            setPosition(botPosition);
            setAddressString(addressString);
            setIsExactCenter(false);

            // ✅ Mark as initialized immediately - bot handled!
            initializationState.current.hasInitialized = true;
            initializationState.current.isInitializing = false;
            setIsInitializing(false);

            log('[initializeGeoLocation] Bot location set:', {
              position: botPosition,
              address: addressString,
            });

            return; // True early return - skip all location detection
          }

          // 🚫 HIGH PRIORITY: Geo-blocked users get the fallback location
          // Check directly with isGeoBlockedRegion() to avoid race condition with state
          const geoBlocked = GEO_FEATURE_FLAGS.ENABLE_GEO_BLOCKING && isGeoBlockedRegion();
          if (geoBlocked) {
            log('[initializeGeoLocation] Geo-blocked, using fallback location');
            const { position: blockedPosition, addressString } = DEFAULT_FALLBACK_LOCATION;

            setPosition(blockedPosition);
            setAddressString(addressString);
            setIsExactCenter(false);

            // ✅ Mark as initialized immediately - geo-blocked handled!
            initializationState.current.hasInitialized = true;
            initializationState.current.isInitializing = false;
            setIsInitializing(false);

            log('[initializeGeoLocation] Geo-blocked location set:', {
              position: blockedPosition,
              address: addressString,
            });

            return; // True early return - skip all location detection
          }

          // Ensure Google Maps is loading in background
          if (!googleMapsLoader.isGoogleMapsLoaded()) {
            googleMapsLoader.ensureLoaded().catch((error) => {
              console.error('Failed to load Google Maps:', error);
            });
          }

          let newPosition: Position | null = null;
          let newAddress = '';
          let newIsExactCenter = false;
          let isFromURL = false;

          console.log({ params });

          // 🏆 HIGHEST PRIORITY: Initial config (if passed)
          if (initialConfig?.position) {
            newPosition = initialConfig.position;
            newAddress = initialConfig.addressString || '';
            newIsExactCenter = initialConfig.isExactCenter ?? true;

            log('[initializeGeoLocation] Using initial config:', {
              position: newPosition,
              address: newAddress,
              isExactCenter: newIsExactCenter,
            });
          }

          // 🥇 HIGH PRIORITY: URL coordinates (only if no initial config)
          if (!newPosition && GEO_FEATURE_FLAGS.USE_URL_COORDINATES) {
            const urlCoords = getURLCoordinates();
            if (urlCoords) {
              newPosition = urlCoords;
              newAddress = ''; // Will geocode
              newIsExactCenter = true;
              isFromURL = true;

              log('[initializeGeoLocation] Using URL coordinates:', {
                coordinates: newPosition,
              });
            }
          }

          // 🥉 HIGH PRIORITY: Cached location (fallback)
          if (!newPosition && GEO_FEATURE_FLAGS.USE_LOCATION_CONTEXT) {
            const stored = locationStorage.getStoredLocation();
            if (stored) {
              newPosition = stored.position;
              newAddress = stored.addressString;
              newIsExactCenter =
                stored.source === 'browser_geolocation' || stored.source === 'user_search';

              log('[initializeGeoLocation] Using cached location:', stored);
            }
          }

          // 🥈 MEDIUM PRIORITY: Profile geometry (if no URL coordinates)
          if (
            !newPosition &&
            GEO_FEATURE_FLAGS.USE_USER_PROFILE_LOCATION &&
            geometry?.lat &&
            geometry?.lng
          ) {
            newPosition = { latitude: geometry.lat, longitude: geometry.lng };
            newAddress = ''; // Always geocode for accuracy
            newIsExactCenter = true;

            log('[initializeGeoLocation] Using profile geometry:', newPosition);
          }

          // Apply synchronous position (single update!)
          if (newPosition) {
            setPosition(newPosition);
            setAddressString(newAddress);
            setIsExactCenter(newIsExactCenter);

            // ✅ Mark as initialized immediately - we have a location!
            initializationState.current.hasInitialized = true;
            initializationState.current.isInitializing = false;
            setIsInitializing(false);

            log('[initializeGeoLocation] Position set from high-priority source:', {
              position: newPosition,
              address: newAddress,
              isFromURL,
              source: initialConfig ? 'initialConfig' : isFromURL ? 'url' : 'other',
            });

            // Geocode if no address (URL, profile coordinates, or initial config without address need geocoding)
            if (!newAddress && (isFromURL || geometry?.lat || initialConfig?.position)) {
              log('[initializeGeoLocation] Starting geocoding for address...');
              setIsAddressPending(true);

              getAddressFromLatLng(newPosition.latitude, newPosition.longitude)
                .then((geocodedAddress) => {
                  if (geocodedAddress) {
                    setAddressString(geocodedAddress);
                  } else {
                    setAddressString(
                      `${newPosition.latitude.toFixed(4)}, ${newPosition.longitude.toFixed(4)}`,
                    );
                  }
                })
                .catch((error) => {
                  console.error('[initializeGeoLocation] Geocoding error:', error);
                  setAddressString(
                    `${newPosition.latitude.toFixed(4)}, ${newPosition.longitude.toFixed(4)}`,
                  );
                })
                .finally(() => {
                  setIsAddressPending(false);
                });
            }
            return; // True early return - skip all lower priorities
          }

          // 🥈 MEDIUM-HIGH PRIORITY: Browser geolocation (if permission granted)
          try {
            const hasPermission = await hasLocationPermission();
            log('[initializeGeoLocation] Permission check:', hasPermission);

            if (hasPermission) {
              try {
                const geo = await requestUserLocation();
                if (geo) {
                  const latitude = geo.coords.latitude;
                  const longitude = geo.coords.longitude;
                  const browserPosition = { latitude, longitude };

                  setPosition(browserPosition);
                  setIsExactCenter(true);

                  // ✅ Mark as initialized immediately - we have precise location!
                  initializationState.current.hasInitialized = true;
                  initializationState.current.isInitializing = false;
                  setIsInitializing(false);

                  log('[initializeGeoLocation] Got precise browser position:', browserPosition);

                  // Get address and store location
                  const address = await getAddressFromLatLng(latitude, longitude);
                  if (address && GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) {
                    locationStorage.setStoredLocation(
                      browserPosition,
                      address,
                      'browser_geolocation',
                    );
                  }
                  return; // True early return - skip all lower priorities
                }
              } catch (error) {
                console.error('Error getting precise browser location:', error);
              }
            }
          } catch (error) {
            console.error('Error checking location permission:', error);
          }

          // 🥉 LOW PRIORITY: Estimated location (fallback)
          log('[initializeGeoLocation] Getting estimated location as fallback');
          try {
            // Use IP-based geolocation if enabled
            if (GEO_FEATURE_FLAGS.USE_IP_GEOLOCATION) {
              try {
                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();

                if (data.latitude && data.longitude) {
                  const ipPosition = {
                    latitude: data.latitude,
                    longitude: data.longitude,
                  };
                  const ipAddressString = `${data.city}, ${data.region_code} ${
                    data.postal || ''
                  }`.trim();

                  setPosition(ipPosition);
                  setAddressString(ipAddressString);
                  setIsExactCenter(false);

                  // ✅ Mark as initialized immediately - we have IP location!
                  initializationState.current.hasInitialized = true;
                  initializationState.current.isInitializing = false;
                  setIsInitializing(false);

                  if (GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) {
                    locationStorage.setStoredLocation(
                      ipPosition,
                      ipAddressString,
                      'ip_geolocation',
                    );
                  }

                  geoCache.setGeocoding(data.latitude, data.longitude, ipAddressString);
                  log('[initializeGeoLocation] Got IP-based position:', ipPosition);
                  return; // True early return - skip Google geolocation
                }
              } catch (error) {
                console.error('IP geolocation failed:', error);
              }
            }

            // Use Google geolocation API as final fallback
            if (skipLocationEstimation) {
              log('[initializeGeoLocation] Skipping location estimation due to flag');

              initializationState.current.hasInitialized = true;
              initializationState.current.isInitializing = false;
              initializationState.current.skippedEstimation = true;
              setIsInitializing(false);

              return; // Early return before expensive calls
            }

            initializationState.current.skippedEstimation = false;
            try {
              const result = await locationManager.getEstimatedLocation(
                sessionId,
                getAddressFromLatLng,
                (position, address) => {
                  log(
                    '[initializeGeoLocation] Address update callback - setting address:',
                    address,
                  );
                  setAddressString(address);
                  setIsAddressPending(false);

                  if (GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) {
                    locationStorage.setStoredLocation(position, address, 'google_estimation');
                  }
                },
              );

              log('[initializeGeoLocation] Location manager returned:', result);

              setPosition(result.position);
              setIsExactCenter(false);

              // ✅ Mark as initialized immediately - we have Google geolocation!
              initializationState.current.hasInitialized = true;
              initializationState.current.isInitializing = false;
              setIsInitializing(false);

              if (result.address) {
                setAddressString(result.address);
                setIsAddressPending(false);

                if (GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) {
                  locationStorage.setStoredLocation(result.position, result.address, result.source);
                }
              } else {
                log(
                  '[initializeGeoLocation] Address pending, will be set when geocoding completes',
                );
                setIsAddressPending(true);
                setAddressString('');

                const timeoutId = setTimeout(() => {
                  setIsAddressPending((pending) => {
                    if (pending) {
                      log(
                        '[initializeGeoLocation] Address geocoding timeout - clearing pending state',
                      );
                      setAddressString('Location selected');
                      return false;
                    }
                    return pending;
                  });
                }, 10000);

                pendingRequestRef.current = { abort: () => clearTimeout(timeoutId) } as any;

                if (GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) {
                  locationStorage.setStoredLocation(result.position, '', result.source);
                }
              }
            } catch (error) {
              console.error('[initializeGeoLocation] Google geolocation failed:', error);
              if (error instanceof Error) {
                setErrorRequestLocation(error);
              } else if (error && typeof error === 'object' && 'code' in error) {
                setErrorRequestLocation(error as GeolocationPositionError);
              } else {
                setErrorRequestLocation(new Error('Failed to get estimated location'));
              }
            }
          } catch (error) {
            console.error('[initializeGeoLocation] Failed to get estimated location:', error);
          }

          // 🚨 If we reach here, no location source worked - mark as initialized anyway
          log('[initializeGeoLocation] No location source succeeded, marking as initialized');
        } finally {
          // ✅ Ensure cleanup happens even if not set elsewhere (error cases)
          if (initializationState.current.isInitializing) {
            initializationState.current.hasInitialized = true;
            initializationState.current.isInitializing = false;
            setIsInitializing(false);
          }
        }
      })();

      return initializationState.current.initializationPromise;
    },
    [sessionId, viewer.isSessionLoading, viewer.userId, position, geometry, getAddressFromLatLng],
  );

  const getURLCoordinates = (): Position | null => {
    if (typeof window === 'undefined') return null; // SSR safety

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const lat = urlParams.get('lat');
      const lng = urlParams.get('lng');

      if (!lat || !lng) return null;

      const latitude = parseFloat(lat);
      const longitude = parseFloat(lng);

      // Validation
      if (isNaN(latitude) || isNaN(longitude)) return null;
      if (latitude < -90 || latitude > 90) return null;
      if (longitude < -180 || longitude > 180) return null;

      return { latitude, longitude };
    } catch (error) {
      console.error('[getURLCoordinates] Error parsing URL:', error);
      return null;
    }
  };

  const updateURL = (position: Position): void => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    url.searchParams.set('lat', position.latitude.toFixed(6));
    url.searchParams.set('lng', position.longitude.toFixed(6));
    window.history.replaceState({}, '', url.toString());
  };

  const onMapDrag = useCallback(
    (position: Position) => {
      setPosition(position);
      setIsExactCenter(true);
      setAddressString('Nearby Areas');

      // ✅ ADD: URL sync for map drag
      if (GEO_FEATURE_FLAGS.PERSIST_COORDINATES_TO_URL) {
        try {
          updateURL(position);
        } catch (error) {
          console.error('[onMapDrag] Failed to sync dragged position to URL:', error);
        }
      }
    },
    [setPosition, setIsExactCenter, setAddressString],
  );

  // Initialize autocomplete manager with debounce time
  useEffect(() => {
    autocompleteManager.initialize(autocompleteDebounceTime);
  }, [autocompleteDebounceTime]);

  useEffect(() => {
    if (!enableAutoInitialization) {
      return;
    }

    // Wait for session to load (determines if user is authenticated or anonymous)
    if (viewer.isSessionLoading) {
      log('[GeoLocationProvider] Session still loading, waiting...');
      return;
    }

    // Skip if we already have a position (prevent overwrites)
    if (position) {
      return;
    }

    log('[GeoLocationProvider] Session loaded:', {
      status: viewer.status,
      userId: viewer.userId,
      hasGeometry: !!geometry,
    });

    // Initialize location with proper priority order
    initializeGeoLocation({ skipLocationEstimation: true }).catch((error) => {
      console.error('[GeoLocationProvider] Initialization failed:', error);
    });
  }, [
    viewer.isSessionLoading,
    viewer.status,
    viewer.userId,
    geometry,
    position,
    initializeGeoLocation,
    enableAutoInitialization,
  ]);

  // localStorage + URL sync effect
  useEffect(() => {
    if (
      GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE &&
      position &&
      addressString &&
      addressString.trim().length > 0
    ) {
      let source:
        | 'user_profile'
        | 'google_estimation'
        | 'browser_geolocation'
        | 'user_search'
        | 'ip_geolocation' = 'google_estimation';

      if (source === 'google_estimation') {
        // Fall back to existing logic
        if (
          geometry?.lat &&
          geometry?.lng &&
          Math.abs(geometry.lat - position.latitude) < 0.0001 &&
          Math.abs(geometry.lng - position.longitude) < 0.0001
        ) {
          source = 'user_profile';
        } else if (isExactCenter) {
          source = 'user_search';
        } else if (hasSetExactLocation) {
          source = 'browser_geolocation';
        }
      }

      log('[GeoLocationProvider] Updating stored location:', {
        position,
        address: addressString,
        source,
      });

      // Store in localStorage
      locationStorage.setStoredLocation(position, addressString, source);
    }
  }, [position, addressString, isExactCenter, hasSetExactLocation, geometry]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (pendingRequestRef.current) {
        pendingRequestRef.current.abort();
      }
      locationManager.unregisterAddressUpdateCallback(sessionId);
    };
  }, [sessionId]);

  const value: GeoLocationContextType = {
    // State
    position,
    addressString,
    exactAddress,
    isExactCenter,
    errorRequestLocation,
    isLoadingAutocomplete,
    isAddressPending,
    isInitializing,
    suggestedAddresses,
    activeAutocompleteAddress,
    autocompleteInputLength,
    hasSetLocationEstimate,
    hasSetExactLocation,
    hasPromptedThisSession,
    centerLatitude: position?.latitude || 0,
    centerLongitude: position?.longitude || 0,
    // Geo-blocking state
    isGeoBlocked,
    geoBlockChecked,

    // Google Maps availability
    isGoogleMapsAvailable: typeof window !== 'undefined' && !!window.google?.maps?.places,

    // Actions
    setPosition,
    setAddressString,
    setExactAddress,
    setIsExactCenter,
    setErrorRequestLocation,
    setHasPromptedThisSession,
    setHasSetLocationEstimate,
    setHasSetExactLocation,
    setSuggestedAddresses,
    setActiveAutocompleteAddress,

    // Business logic
    initializeGeoLocation,
    requestUserLocationAndUpdateState,
    getEstimatedLocation,
    getAddressFromLatLng,
    getAutocompletePlaces,
    getImmediateAutcompletePlaces,
    handleSubmitAutocomplete,
    getExactDetails,

    // Business convenience methods
    updateAddressAndClearAutocomplete,
    selectAutocompleteSuggestion,
    clearAutocompleteState,
    resetLocationState,
    shouldShowAutocomplete,
    isSearchingAutocomplete,
    onMapDrag,
  };

  return <GeoLocationContext.Provider value={value}>{children}</GeoLocationContext.Provider>;
};

export const useGeoLocationState = () => {
  const context = useContext(GeoLocationContext);
  if (context === undefined) {
    throw new Error('useGeoLocationState must be used within a GeoLocationProvider');
  }
  return context;
};
